// src/components/ComparisonPanel.jsx
import React, { useMemo, memo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Chip,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PamGrid, GridConfigMenu, useGridConfigs } from '@1823-partners/core';
import api from '../api';
import {
  getDeltaCellStyle,
  GATED_CASHFLOW_FIELDS,
  buildCashflowRowData,
  buildCashflowMonthlyRowData,
} from '../utils/comparisonUtils';
import { columnHasData, formatCouponPercent } from '../utils/cashflowDisplay';
import { orderAnalyticsRows } from '../utils/analyticsRows';

const ComparisonPanel = memo(({
  comparisonData,
  loading = false,
  onClose,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCfTranche, setSelectedCfTranche] = useState('');
  // Both cashflow and cashflowMonthly are already in comparisonData, so
  // toggling grain is a pure re-pivot - no refetch. Yearly is the default,
  // matching today's behavior on first render.
  const [cfGrain, setCfGrain] = useState('year');

  // One grid ref + one view-options controller per grid. The analytics and
  // cashflow grids live on separate tabs but each gets its own gridId so saved
  // layouts don't collide.
  const analyticsGridRef = useRef(null);
  const cashflowGridRef = useRef(null);

  const analyticsGridConfigs = useGridConfigs(api, {
    app: 'trade',
    gridId: 'comparison-analytics',
    gridApiRef: analyticsGridRef,
  });
  const cashflowGridConfigs = useGridConfigs(api, {
    app: 'trade',
    gridId: 'comparison-cashflow',
    gridApiRef: cashflowGridRef,
  });

  const scenarios = useMemo(() => {
    if (!comparisonData?.scenarios) return {};
    return comparisonData.scenarios;
  }, [comparisonData]);

  const scenarioLabels = useMemo(() => Object.keys(scenarios), [scenarios]);
  const shockBps = comparisonData?.shock_bps;

  // Extract all tranche names from the analytics data across all scenarios
  const allTranches = useMemo(() => {
    const trancheSet = new Set();
    Object.values(scenarios).forEach(scenData => {
      const analytics = scenData?.analytics;
      if (Array.isArray(analytics) && analytics.length > 0) {
        const firstRow = analytics[0];
        Object.keys(firstRow || {}).forEach(key => {
          const match = key.match(/^Tranche\s+([A-Z0-9]+):/i);
          if (match) trancheSet.add(match[1]);
        });
      }
    });
    return Array.from(trancheSet);
  }, [scenarios]);

  // Set default cashflow tranche
  useMemo(() => {
    if (!selectedCfTranche && allTranches.length > 0) {
      setSelectedCfTranche(allTranches[0]);
    }
  }, [allTranches, selectedCfTranche]);

  // ─── Analytics Comparison Tab ───────────────────────────────────────
  const analyticsColDefs = useMemo(() => {
    if (scenarioLabels.length === 0) return [];

    const cols = [
      {
        headerName: 'Metric',
        field: 'Metric',
        pinned: 'left',
        width: 200,
        sortable: true,
        filter: true,
        cellStyle: { fontWeight: 'bold' },
      },
    ];

    // Add columns for each scenario
    scenarioLabels.forEach(label => {
      const children = allTranches.map(tranche => {
        const fieldKey = `${label}__${tranche}`;
        return {
          headerName: tranche,
          field: fieldKey,
          width: 120,
          sortable: true,
          valueFormatter: ({ value }) => {
            if (value === null || value === undefined || isNaN(value)) return '--';
            if (typeof value === 'number') {
              return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
            }
            return value;
          },
        };
      });

      cols.push({
        headerName: label,
        children,
      });
    });

    // Add delta columns (shocked - base) if we have a "Base" scenario
    if (scenarios['Base']) {
      const shockedLabels = scenarioLabels.filter(l => l !== 'Base');
      shockedLabels.forEach(label => {
        const children = allTranches.map(tranche => {
          const fieldKey = `delta__${label}__${tranche}`;
          return {
            headerName: tranche,
            field: fieldKey,
            width: 120,
            sortable: true,
            valueFormatter: ({ value }) => {
              if (value === null || value === undefined || isNaN(value)) return '--';
              const sign = value > 0 ? '+' : '';
              return `${sign}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
            },
            cellStyle: (params) => {
              const metricName = params.data?.Metric || '';
              return getDeltaCellStyle(metricName, params.value);
            },
          };
        });

        cols.push({
          headerName: `Delta (${label} - Base)`,
          children,
        });
      });
    }

    return cols;
  }, [scenarioLabels, allTranches, scenarios]);

  const analyticsRowData = useMemo(() => {
    if (scenarioLabels.length === 0 || allTranches.length === 0) return [];

    // Get all metrics from the first scenario that has data
    const firstScenWithData = scenarioLabels.find(label => {
      const analytics = scenarios[label]?.analytics;
      return Array.isArray(analytics) && analytics.length > 0;
    });
    if (!firstScenWithData) return [];

    const firstAnalytics = scenarios[firstScenWithData].analytics;
    const metrics = firstAnalytics.map(row => row.Metric).filter(Boolean);

    return orderAnalyticsRows(metrics.map(metric => {
      const row = { Metric: metric };

      scenarioLabels.forEach(label => {
        const analytics = scenarios[label]?.analytics;
        if (!Array.isArray(analytics)) return;
        const metricRow = analytics.find(r => r.Metric === metric);
        if (!metricRow) return;

        allTranches.forEach(tranche => {
          // Find the column that matches this tranche
          const matchingKey = Object.keys(metricRow).find(
            k => k.startsWith(`Tranche ${tranche}:`)
          );
          const value = matchingKey ? metricRow[matchingKey] : null;
          row[`${label}__${tranche}`] = typeof value === 'number' ? value : null;
        });
      });

      // Compute deltas (shocked - base)
      if (scenarios['Base']) {
        const shockedLabels = scenarioLabels.filter(l => l !== 'Base');
        shockedLabels.forEach(label => {
          allTranches.forEach(tranche => {
            const shocked = row[`${label}__${tranche}`];
            const base = row[`Base__${tranche}`];
            if (typeof shocked === 'number' && typeof base === 'number') {
              row[`delta__${label}__${tranche}`] = shocked - base;
            } else {
              row[`delta__${label}__${tranche}`] = null;
            }
          });
        });
      }

      return row;
    }));
  }, [scenarioLabels, allTranches, scenarios]);

  // ─── Cashflow Comparison Tab ────────────────────────────────────────
  const dollarFormatter = ({ value }) =>
    value != null ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--';

  // Raw rows for the selected tranche, per scenario, at the current grain -
  // shared by cashflowColDefs (to gate the extra columns) and cashflowRowData
  // (to pivot), so the two can never disagree on what's "displayed".
  const cfFilteredRowsByScenario = useMemo(() => {
    const isMonthly = cfGrain === 'month';
    const sourceField = isMonthly ? 'cashflowMonthly' : 'cashflow';
    const result = {};
    scenarioLabels.forEach(label => {
      const cfData = scenarios[label]?.[sourceField];
      result[label] = Array.isArray(cfData)
        ? cfData.filter(row => (row.TRANCHE || row.Tranche) === selectedCfTranche)
        : [];
    });
    return result;
  }, [scenarioLabels, scenarios, selectedCfTranche, cfGrain]);

  // WRITEDOWN / INTEREST_SHORTFALL / PREPAY_PENALTY are genuinely zero for
  // most bonds we hold - only add a scenario's column when at least one
  // displayed row (any scenario) actually carries a value, same gate (and
  // same field list) as the shared cashflow table in comparisonUtils.js.
  const gatedCfFields = useMemo(() => {
    const allRows = Object.values(cfFilteredRowsByScenario).flat();
    return GATED_CASHFLOW_FIELDS.filter(([field]) => columnHasData(allRows, field));
  }, [cfFilteredRowsByScenario]);

  const cashflowColDefs = useMemo(() => {
    if (scenarioLabels.length === 0) return [];

    const isMonthly = cfGrain === 'month';
    const cols = [
      isMonthly
        ? { headerName: 'Date', field: 'DATE', pinned: 'left', width: 100, sortable: true }
        : { headerName: 'Year', field: 'YEAR', pinned: 'left', width: 80, sortable: true },
    ];

    scenarioLabels.forEach(label => {
      cols.push({
        headerName: label,
        children: [
          { headerName: 'Principal', field: `${label}__PRINCIPAL`, width: 130, valueFormatter: dollarFormatter },
          { headerName: 'Interest', field: `${label}__INTEREST`, width: 130, valueFormatter: dollarFormatter },
          { headerName: 'Balance', field: `${label}__BALANCE`, width: 130, valueFormatter: dollarFormatter },
          ...gatedCfFields.map(([field, name]) => ({
            headerName: name,
            field: `${label}__${field}`,
            width: 130,
            valueFormatter: dollarFormatter,
          })),
          {
            headerName: 'Coupon',
            field: `${label}__COUPON`,
            width: 110,
            // A rate, not a dollar amount - percent formatting, never summed.
            valueFormatter: ({ value }) => (value == null ? '--' : formatCouponPercent(value)),
          },
        ],
      });
    });

    return cols;
  }, [scenarioLabels, cfGrain, gatedCfFields]);

  // Pivoting is delegated to the shared builders rather than done locally:
  // a bespoke "last matching row wins" pivot here previously let a tranche
  // with several CUSIPs in one period silently pick an arbitrary row instead
  // of summing the dollar fields / balance-weighting COUPON, which the
  // shared builders already get right (and cover in comparisonUtils.test.js).
  const cashflowRowData = useMemo(() => {
    if (scenarioLabels.length === 0 || !selectedCfTranche) return [];
    const pivot = cfGrain === 'month' ? buildCashflowMonthlyRowData : buildCashflowRowData;
    return pivot(scenarios, scenarioLabels, selectedCfTranche);
  }, [scenarioLabels, scenarios, selectedCfTranche, cfGrain]);

  if (!comparisonData) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header with title and close */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}
          >
            {`Shock Comparison (${shockBps}bps)`}
          </Typography>
          {scenarioLabels.map(label => (
            <Chip
              key={label}
              label={label}
              size="small"
              sx={{
                fontSize: '11px',
                height: 22,
                bgcolor: label === 'Base'
                  ? alpha(theme.palette.info.main, 0.15)
                  : alpha(theme.palette.warning.main, 0.15),
                color: label === 'Base'
                  ? theme.palette.info.dark
                  : theme.palette.warning.dark,
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          minHeight: 32,
          mb: 1,
          '& .MuiTab-root': {
            minHeight: 32,
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
          },
        }}
      >
        <Tab label="Analytics Comparison" />
        <Tab label="Cashflow Comparison" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <GridConfigMenu controller={analyticsGridConfigs} />
          </Box>
          <Box
            sx={{ height: 500 }}
            style={{
              '--ag-font-family': '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              '--ag-font-size': '13px',
              '--ag-header-font-weight': '600',
            }}
          >
            <PamGrid
              ref={analyticsGridRef}
              rowData={analyticsRowData}
              columnDefs={analyticsColDefs}
              defaultColDef={{
                flex: 0,
                sortable: true,
                filter: true,
                resizable: true,
              }}
              showToolbar={false}
              showExport={false}
              rowHeight={32}
              headerHeight={36}
              height="100%"
              containerSx={{ height: '100%' }}
              gridContainerSx={{ height: '100%' }}
              onGridReady={analyticsGridConfigs.onGridReady}
              onFilterChanged={analyticsGridConfigs.onGridStateChanged}
              agGridProps={{
                groupHeaderHeight: 32,
                suppressHorizontalScroll: false,
                animateRows: false,
                enableRangeSelection: true,
                ...analyticsGridConfigs.stashBindings,
              }}
            />
          </Box>
        </>
      )}

      {!loading && activeTab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {allTranches.length > 1 ? (
                <FormControl size="small" sx={{ maxWidth: 200 }}>
                  <InputLabel>Tranche</InputLabel>
                  <Select
                    value={selectedCfTranche}
                    onChange={(e) => setSelectedCfTranche(e.target.value)}
                    label="Tranche"
                    sx={{ fontSize: '13px' }}
                  >
                    {allTranches.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box />
              )}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cfGrain}
                onChange={(_, value) => value && setCfGrain(value)}
                aria-label="Cashflow grain"
              >
                <ToggleButton value="month">Monthly</ToggleButton>
                <ToggleButton value="year">Yearly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <GridConfigMenu controller={cashflowGridConfigs} />
          </Box>
          <Box
            sx={{ height: 500 }}
            style={{
              '--ag-font-family': '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              '--ag-font-size': '13px',
              '--ag-header-font-weight': '600',
            }}
          >
            <PamGrid
              ref={cashflowGridRef}
              rowData={cashflowRowData}
              columnDefs={cashflowColDefs}
              defaultColDef={{
                flex: 0,
                sortable: true,
                filter: true,
                resizable: true,
              }}
              showToolbar={false}
              showExport={false}
              rowHeight={32}
              headerHeight={36}
              height="100%"
              containerSx={{ height: '100%' }}
              gridContainerSx={{ height: '100%' }}
              onGridReady={cashflowGridConfigs.onGridReady}
              onFilterChanged={cashflowGridConfigs.onGridStateChanged}
              agGridProps={{
                groupHeaderHeight: 32,
                suppressHorizontalScroll: false,
                animateRows: false,
                enableRangeSelection: true,
                ...cashflowGridConfigs.stashBindings,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
});

ComparisonPanel.displayName = 'ComparisonPanel';

export default ComparisonPanel;
